import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { BillingFeature } from 'webapp/enums/enums';
import { copyObject } from 'webapp/utils/common';

import safeStyleHelper from 'webapp/helpers/safe-style';
import CommonRequestTrialLinkComponent from 'webapp/components/common/request-trial-link';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import CommonUpgradePlanLinkComponent from 'webapp/components/common/upgrade-plan-link';
import ToolTipComponent from 'webapp/components/tool-tip';

import type { BasicFeatureInfo } from 'webapp/models-custom/active-features';

interface ExtendedFeatureInfo extends BasicFeatureInfo {
	isActive: boolean;
	canBeUpgradedWithPlan: boolean;
	defaultValue?: number | boolean | null;
	showCleanupOption?: boolean;
	isOverDefault?: boolean;
	isCustomized?: boolean;
	isLimitReached?: boolean;
	isOverLimit?: boolean;
	canRequestTrial?: boolean;
	tooltipText?: string;
	icon?: IconName;
	showStatus?: boolean;
	showUsageByAccountLink?: boolean;
	barData?: {
		percent: string;
		color: string;
		text?: string;
	};
}

export default class BillingFeaturesComponent extends Component {
	@service('billing') declare billingService: Service.Billing;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('model-manager/spaces') declare mmSpacesService: Service.ModelManager.Spaces;
	@service('account') declare accountService: Service.Account;
	@service('flow/promotions') declare flowPromotionsService: Service.Flow.Promotions;

	origin = 'billing_page_features';

	@tracked isListExtended = false;

	get mainFeatureIds() {
		const mainFeatures = [BillingFeature.SOURCES, BillingFeature.USERS];
		if (this.accountService.isAgency) {
			mainFeatures.push(BillingFeature.CLIENTS);
		}
		mainFeatures.push(BillingFeature.AI_CREDITS);
		return mainFeatures;
	}

	@cached
	get features() {
		const featureList: ExtendedFeatureInfo[] = [];

		const featuresOrder = [
			...this.mainFeatureIds,
			BillingFeature.DATA_REFRESH_INTERVAL,
			BillingFeature.HISTORICAL_DATA,
			BillingFeature.ADVANCED_DATA_SOURCES,
			BillingFeature.SHEET_SOURCES,
			BillingFeature.BOARDS,
			BillingFeature.BOARD_LOOPS,
			BillingFeature.CUSTOM_METRICS,
			BillingFeature.GOALS,
			BillingFeature.OKR,
			BillingFeature.REPORTS_BASIC,
			BillingFeature.MFA,
			BillingFeature.SHARING,
			BillingFeature.ADVANCED_SECURITY,
		];

		const activePlan = this.billingService.activePlan!;
		const activeFeatures = this.billingService.features;

		featuresOrder.forEach((featureId) => {
			const baseFeatureInfo = this.billingService.getFeatureInfo(featureId);
			const currentFeatureInfo = activeFeatures.getFeatureInfo(featureId);

			if (!baseFeatureInfo || !currentFeatureInfo) {
				return;
			}

			// isActive and canBeUpgradedWithPlan are set below
			const info = copyObject(baseFeatureInfo) as ExtendedFeatureInfo;

			// formattedValue
			if (featureId === BillingFeature.SOURCES) {
				info.formattedValue = String(info.current);
				if (!activePlan.isExtraSourcesAllowedOnPlan) {
					info.formattedValue += ` / ${info.value === null ? '∞' : info.value}`;
				}
			} else if (!info.formattedValue) {
				if (info.value === true) {
					// "enabled"
					info.formattedValue = ' ';
				} else {
					info.formattedValue = info.value === null ? '∞' : String(info.value);
					if (typeof info.current !== 'undefined') {
						info.formattedValue = `${info.current} / ${info.formattedValue}`;
					}
				}
			}

			const showBar = this.mainFeatureIds.includes(featureId);
			if (showBar) {
				let text = ''; // text shown under bar
				if (featureId === BillingFeature.AI_CREDITS) {
					text = this.billingService.aiCreditsInfoText;
					info.showUsageByAccountLink =
						this.billingService.canViewAiCreditsUsageByAccount;
				}
				const currentValue = info.current || 0,
					isUnlimited = typeof info.value !== 'number',
					maxValue = isUnlimited ? currentValue : (info.value as number);
				const percent = maxValue ? (currentValue / maxValue) * 100 : 100;
				info.barData = {
					percent: Math.round(Math.min(100, percent)) + '%',
					color: isUnlimited ? 'neutral' : percent >= 100 ? 'red' : 'green',
					text,
				};
			}

			info.isActive = info.value !== false && info.value !== 0;

			// checking if we have manually customized the value
			// comparing default (plan) value with the given
			let defaultValue = currentFeatureInfo.value;
			if (featureId === BillingFeature.BOARDS) {
				defaultValue = activeFeatures.boardsMax;
			} else if (featureId === BillingFeature.CLIENTS) {
				defaultValue = activeFeatures.clientsMax;
			} else if (featureId === BillingFeature.REPORTS_BASIC) {
				defaultValue = activeFeatures.reportsMax;
			} else if (featureId === BillingFeature.SOURCES) {
				defaultValue = (info.value as number) - this.billingService.sourcesGivenInPA;
			}
			info.defaultValue = defaultValue;

			// if the user has more already than the plan default max
			info.isOverDefault = this.billingService.compareFeatureValues(
				featureId,
				defaultValue as number,
				info.current!,
			);

			// if the max is more than the plan default max
			info.isCustomized = this.billingService.compareFeatureValues(
				featureId,
				defaultValue as number,
				info.value as number,
			);

			// if is at or over max
			info.isLimitReached = !this.billingService.checkIfCan(featureId);

			info.isOverLimit =
				activeFeatures.isCountable(featureId) &&
				info.value !== null &&
				(info.value as number) < info.current!;

			info.showCleanupOption = featureId === BillingFeature.CUSTOM_METRICS;

			info.canBeUpgradedWithPlan =
				this.billingService.canFeatureBeUpgradedWithPlan(featureId);

			if (
				featureId !== BillingFeature.CUSTOM_METRICS &&
				featureId !== BillingFeature.AI_CREDITS &&
				featureId !== BillingFeature.USERS &&
				featureId !== BillingFeature.SOURCES
			) {
				const planForFeature = this.billingService.getNextPlanForFeature(featureId);

				info.canRequestTrial = planForFeature
					? !!this.flowPromotionsService.getAvailableTrialPromotion(planForFeature)
					: false;
			}

			let icon: IconName | undefined;
			if (activePlan.isFree) {
				if (info.isOverDefault || info.isCustomized) {
					icon = 'AlertFill';
				}
			} else if (info.isActive) {
				icon = 'Check';
			}

			if (info.isCustomized) {
				icon = 'Gift';

				let tooltipText = '';

				if (
					info.id &&
					[BillingFeature.HISTORICAL_DATA, BillingFeature.DATA_REFRESH_INTERVAL].includes(
						info.id,
					)
				) {
					tooltipText += `${info.formattedValue} ${info.name}`;
				} else if (
					info.id &&
					[
						BillingFeature.SOURCES,
						BillingFeature.BOARDS,
						BillingFeature.CUSTOM_METRICS,
						BillingFeature.USERS,
					].includes(info.id)
				) {
					const value = info.value ? +info.value : 0,
						defaultValue = info.defaultValue ? +info.defaultValue : 0;

					tooltipText += `${value - defaultValue} ${info.name}`;
				} else {
					tooltipText += '&nbsp;';
				}

				tooltipText = `<strong class="icon-tooltip"><ui-icon name="${icon}" size="xs"></ui-icon>${tooltipText}</strong>`;
				tooltipText += 'Features provided on your plan at no additional charge.';

				info.tooltipText = tooltipText;
			}

			info.icon = icon;

			info.showStatus = info.icon !== 'Check' || !info.barData;

			if (info.id === BillingFeature.GENERATIVE_AI) {
				info.name = 'Performance summaries';
			}

			featureList.push(info);
		});

		return featureList;
	}

	get shownFeatures() {
		if (this.isListExtended) {
			return this.features;
		} else {
			return this.features.filter((f) => {
				return f.id && this.mainFeatureIds.includes(f.id);
			});
		}
	}

	@action
	getUpgradeText(featureId?: BillingFeature) {
		return featureId === BillingFeature.AI_CREDITS ? 'Manage' : 'Upgrade';
	}

	@action
	onOpenCleanup() {
		void this.mmMetricsService.openUnusedMetricCleanupPopup();
	}

	@action
	onMouseHover() {
		const cleanUpButtonElements = document.querySelectorAll(
			'.features .feature__name .cleanup-link',
		);
		Array.from(cleanUpButtonElements).forEach((element) => {
			element.classList.toggle('hovered');
		});
	}

	@action
	openAiCreditsUsage() {
		this.billingService.openAiCreditsUsagePopup();
	}

	@action
	toggleList() {
		this.isListExtended = !this.isListExtended;
	}

	<template>
		<div class="features">
			<h1>Usage & limits</h1>
			<div class="feature__list {{if this.isListExtended 'feature__list--extended'}}">
				{{#each this.shownFeatures as |feature|}}
					<div class="feature {{if feature.isActive 'active'}} limit--{{feature.icon}}">
						<div class="line--1">

							{{#if feature.showStatus}}
								<div class="status">
									{{#if feature.icon}}
										<CommonUiIconComponent @name={{feature.icon}} @size="xs" />
									{{else}}
										<CommonUiIconComponent @name="Minus" @size="xs" />
									{{/if}}
									{{#if feature.tooltipText}}
										<ToolTipComponent
											@text={{feature.tooltipText}}
											@position="top"
											@extraClass="billing-feature-tooltip"
											@theme="light"
											@maxWidth={{230}}
										/>
									{{/if}}
								</div>
							{{/if}}

							<div class="feature__name">
								{{feature.name}}
								{{#if feature.showCleanupOption}}
									<a
										role="button"
										class="cleanup-link"
										{{on "click" this.onOpenCleanup}}
										{{on "mouseenter" this.onMouseHover}}
										{{on "mouseleave" this.onMouseHover}}
									>
										<CommonUiIconComponent @name="Purge" @size="sm" />
										Clean-up
									</a>
								{{/if}}
							</div>

							<div class="feature__other">
								{{#if feature.isActive}}
									<div class={{if feature.isLimitReached "limit-reached"}}>
										{{feature.formattedValue}}
									</div>
								{{else}}
									<div><CommonUiIconComponent @name="Minus" @size="xs" /></div>
								{{/if}}

								<div>
									{{#if feature.canRequestTrial}}
										<CommonRequestTrialLinkComponent
											@feature={{feature.id}}
											@trackingOrigin={{this.origin}}
										/>
									{{else if feature.canBeUpgradedWithPlan}}
										<CommonUpgradePlanLinkComponent
											@text={{this.getUpgradeText feature.id}}
											@feature={{feature.id}}
											@trackingOrigin={{this.origin}}
										/>
									{{/if}}
								</div>
							</div>
						</div>

						{{#if feature.barData}}
							<div class="line--2">
								<div class="bar color--{{feature.barData.color}}">
									<span
										class="bar__value"
										style={{safeStyleHelper
											feature.barData.percent
											property="width"
										}}
									></span>
								</div>
								{{#if feature.barData.text}}
									<p>{{feature.barData.text}}</p>
								{{/if}}
								{{#if feature.showUsageByAccountLink}}
									<a
										role="button"
										class="usage-by-account-link"
										{{on "click" this.openAiCreditsUsage}}
									>
										<CommonUiIconComponent @name="ChartBar" @size="sm" />
										Usage by account
									</a>
								{{/if}}
							</div>
						{{/if}}

					</div>
				{{/each}}
				<a class="feature__list__expand-toggle" {{on "click" this.toggleList}}>
					{{#if this.isListExtended}}Show fewer features{{else}}Show all included features{{/if}}
				</a>
			</div>
		</div>
	</template>
}
